import axios from "axios";
import { IToken } from "../interfaces/IToken";

const api = axios.create({});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const saveRedirectUrl = () => {
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.pathname !== "/login" &&
    window.location.pathname !== "/cadastro" &&
    window.location.pathname !== "/confirmar-email"
  ) {
    localStorage.setItem(
      "redirectAfterLogin",
      window.location.pathname + (window.location.search || ""),
    );
  }
};

const addTokenToRequestConfig = (config: any) => {
  config.headers = config.headers || {};
  if (!config.headers.Authorization) {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      try {
        const parsedToken: IToken = JSON.parse(userToken);
        if (parsedToken?.token_access) {
          config.headers.Authorization = `Bearer ${parsedToken.token_access}`;
        }
      } catch {
        // Ignora erro de JSON malformado
      }
    }
  }
  return config;
};

api.interceptors.request.use(addTokenToRequestConfig);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        const originalRequest = error.config;

        const isRefreshRequest =
          typeof originalRequest?.url === "string" &&
          originalRequest.url.includes("/acesso/token-refresh");

        if (isRefreshRequest || originalRequest?._retry) {
          localStorage.removeItem("userToken");
          saveRedirectUrl();
          redirectToLogin();
          return Promise.reject(error);
        }

        let refreshTokenValue: string | null = null;
        try {
          const userTokenStr = localStorage.getItem("userToken");
          if (userTokenStr) {
            const parsed = JSON.parse(userTokenStr);
            refreshTokenValue = parsed?.token_refresh || null;
          }
        } catch {
          refreshTokenValue = null;
        }

        if (!refreshTokenValue) {
          saveRedirectUrl();
          redirectToLogin();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshUrl =
            (process.env.REACT_APP_API_BASE_URL_IDENTITY || "") +
            "/acesso/token-refresh";

          const response = await api.post(refreshUrl, {
            token_refresh: refreshTokenValue,
          });

          const tokenData = response.data?.data;
          if (tokenData?.token_access) {
            localStorage.setItem("userToken", JSON.stringify(tokenData));
            processQueue(null, tokenData.token_access);

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${tokenData.token_access}`;
            return api(originalRequest);
          } else {
            throw new Error("Falha ao renovar token");
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem("userToken");
          saveRedirectUrl();
          redirectToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      if (error.response.status === 400) {
        const isBlob = error.response.data instanceof Blob;
        if (isBlob) {
          const blob = error.response.data as Blob;
          const isJsonBlob = blob.type === "application/json";
          if (isJsonBlob) {
            return blob.text().then((text) => {
              let json;
              try {
                json = JSON.parse(text);
              } catch {
                json = {
                  success: false,
                  notifications: [
                    { message: "Erro ao converter Blob para JSON" },
                  ],
                };
              }
              return {
                ...error.response,
                data: {
                  success: false,
                  notifications: json.notifications || [],
                },
              };
            });
          }
          return Promise.resolve({
            ...error.response,
            data: {
              success: false,
              notifications: [{ message: "Blob recebido não é JSON" }],
            },
          });
        }
        return Promise.resolve({
          ...error.response,
          data: {
            success: false,
            notifications: error.response.data.notifications || [],
          },
        });
      } else {
        console.error("Erro de resposta:", error);

        const formattedError = {
          success: false,
          notifications: [
            {
              tag: "response_error",
              key: "status_code",
              message: `Erro de resposta: ${error.response.status}`,
            },
          ],
        };

        return Promise.resolve({
          data: formattedError,
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          config: error.config,
        });
      }
    } else if (error.request) {
      console.error("Erro de solicitação:", error);
      const formattedError = {
        success: false,
        notifications: [
          {
            tag: "request_error",
            key: "no_response",
            message: "A solicitação não recebeu resposta",
          },
        ],
      };

      return Promise.resolve({
        data: formattedError,
        status: 500,
        statusText: "Ocorreu erro interno do servidor, acionar suporte.",
        headers: {},
        config: error.config,
      });
    } else {
      console.error("Erro geral:", error);

      const formattedError = {
        success: false,
        notifications: [
          {
            tag: "general_error",
            key: "setup_failure",
            message: `Erro geral: ${error.message}`,
          },
        ],
      };

      return Promise.resolve({
        data: formattedError,
        status: 500,
        statusText: "Ocorreu erro interno do servidor, acionar suporte.",
        headers: {},
        config: error.config,
      });
    }
  },
);

export const redirectToLogin = () => {
  window.location.href = "/login";
};

export default api;
